
import { MapPin, Navigation, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import LocationDisplay from "./LocationDisplay";
import UserTimezoneDisplay from "./UserTimezoneDisplay";
import { PlacesAutocomplete } from '@/components/PlacesAutocomplete';

interface DistanceFilterProps {
  distance: string;
  onDistanceChange: (value: string) => void;
  userLocation: {lat: number, lng: number} | null;
  locationError: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  isGettingLocation: boolean;
  isLoadingAddress: boolean;
  timezoneDisplay: string | null;
  isApproximateLocation?: boolean;
  requestLocationAccess: () => void;
  setManualLocation?: (lat: number, lng: number, address: string) => void;
  isGeolocationPolicyBlocked?: () => boolean;
}

const DistanceFilter = ({
  distance,
  onDistanceChange,
  userLocation,
  locationError,
  city,
  state,
  country,
  isGettingLocation,
  isLoadingAddress,
  timezoneDisplay,
  isApproximateLocation = false,
  requestLocationAccess,
  setManualLocation,
  isGeolocationPolicyBlocked
}: DistanceFilterProps) => {
  const { t } = useTranslation('practitioners');

  const handleDistanceChange = (value: string) => {
    onDistanceChange(value);
    if (value !== "any_distance" && !userLocation && !locationError && !isGettingLocation) {
      requestLocationAccess();
    }
  };

  return (
    <div>
      <h3 className="text-sm font-medium mb-2 flex items-center">
        <MapPin className="mr-2 h-4 w-4 text-amber-600" /> {t('distance')}
      </h3>
      
      <Select value={distance} onValueChange={handleDistanceChange}>
        <SelectTrigger className="border-amber-200">
          <SelectValue placeholder={t('anyDistance')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any_distance">{t('anyDistance')}</SelectItem>
          <SelectItem value="5">{t('within5Miles')}</SelectItem>
          <SelectItem value="10">{t('within10Miles')}</SelectItem>
          <SelectItem value="25">{t('within25Miles')}</SelectItem>
          <SelectItem value="50">{t('within50Miles')}</SelectItem>
        </SelectContent>
      </Select>

      {distance !== "any_distance" && !userLocation && !locationError && !isGettingLocation && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700 mb-2">
            {t('locationAccessRequired')}
          </p>
          
          {/* Show manual address selection */}
          {setManualLocation && (
            <div className="mb-3">
              <PlacesAutocomplete
                onPlaceSelect={(lat, lng, address) => {
                  setManualLocation(lat, lng, address);
                }}
                placeholder="Enter your address for distance calculations..."
              />
              <div className="text-center text-xs text-amber-600 my-2">or</div>
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={requestLocationAccess}
            className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            <Navigation className="mr-2 h-4 w-4" />
            {t('enableLocationAccess')}
          </Button>
        </div>
      )}

      {(isGettingLocation || isLoadingAddress) && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center text-blue-700 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {isGettingLocation ? t('gettingYourLocation') : t('lookingUpYourAddress')}
          </div>
        </div>
      )}

      {!userLocation && !isGettingLocation && locationError && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700 mb-3">
            {locationError}
          </p>
          
          {/* Show manual address selection if GPS is blocked or failed */}
          {setManualLocation && (
            <div className="mb-3">
              <PlacesAutocomplete
                onPlaceSelect={(lat, lng, address) => {
                  setManualLocation(lat, lng, address);
                }}
                placeholder="Enter your address for distance calculations..."
              />
              <div className="text-center text-xs text-amber-600 my-2">or</div>
            </div>
          )}
          
          <Button 
            onClick={requestLocationAccess}
            className="w-full"
            variant="outline"
            size="sm"
          >
            <MapPin className="w-4 h-4 mr-2" />
            {t('tryAgain')}
          </Button>
        </div>
      )}

      {(userLocation || isGettingLocation || isLoadingAddress || locationError) && (
        <LocationDisplay
          city={city}
          state={state}
          country={country}
          isLoading={isLoadingAddress}
          hasError={!!locationError}
          isApproximate={isApproximateLocation}
          onRefresh={requestLocationAccess}
        />
      )}

      <UserTimezoneDisplay
        timezoneDisplay={timezoneDisplay}
        onRefresh={requestLocationAccess}
      />
    </div>
  );
};

export default DistanceFilter;
